import { AbstractControl } from '@angular/forms';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { IOptionCategory } from '../models/option-category.interface';

interface IConcernOptionValue {
  id: number;
  isModality?: boolean;
  isTopSelection?: boolean;
}

function sortDropdownOptionsByName<T>(
  option1: DropdownOption<T | null>, option2: DropdownOption<T | null>
): number {
  if (option1 === null) { return -1 }
  if (option2 === null) { return 1 }

  return option1.name.localeCompare(option2.name);
}

function sortDropdownOptionsByTopSelectionFirst<T>(
  option1: DropdownOption<T | null>, option2: DropdownOption<T | null>
): number {
  if (option1 === null) { return -1 }
  if (option2 === null) { return 1 }

  const opt1 = option1 as DropdownOption<IConcernOptionValue>;
  const opt2 = option2 as DropdownOption<IConcernOptionValue>;

  if (opt1.value.isTopSelection && opt2.value.isTopSelection) {
    return opt1.name.localeCompare(opt2.name);
  }
  if (!opt1.value.isTopSelection && !opt2.value.isTopSelection) {
    return opt1.name?.localeCompare(opt2?.name);
  }

  return Number(opt2.value.isTopSelection) - Number(opt1.value.isTopSelection);
}

function listWithSelectionsFirst<T>(
  allSortedOptions: Array<DropdownOption<T>>, control: AbstractControl<T[]>
): Array<DropdownOption<T | null>> {
  const selectionsMap = new Map(control.value.map((selection: T) => [selection, true]));

  const selectedOptions: Array<DropdownOption<T | null>> = [];
  const nonSelectedOptions: Array<DropdownOption<T | null>> = [];

  allSortedOptions.forEach((option) => {
    const isOptionSelected = option.value && selectionsMap.has(option.value);
    if (isOptionSelected) {
      selectedOptions.push(option);
    } else {
      nonSelectedOptions.push(option);
    }
  });
  return selectedOptions.sort(sortDropdownOptionsByName).concat(nonSelectedOptions);
}

function arrangeOptions<T>(
  category: IOptionCategory<T | null>,
  control: AbstractControl,
  showAll: boolean,
  hasTopSelection: boolean
): Array<DropdownOption<T | null>> {
  const shouldPutSelectionsAtTopOfList = !!category.trimmedListCount // A trimmed list count is specified,
      && category.trimmedListCount < category.options.length // there are more options than the trimmed list count,
      && !!control.value.length // there is at least one selection,
      && !showAll; // And we're not displaying all options.

  const allSortedOptions = hasTopSelection && !showAll
    ? category.options.sort(sortDropdownOptionsByTopSelectionFirst)
    : category.options.sort(sortDropdownOptionsByName);

  return shouldPutSelectionsAtTopOfList
    ? listWithSelectionsFirst(allSortedOptions, control)
    : allSortedOptions;
}

export function sortCategoryOptions<T>(
  categories: Array<IOptionCategory<T | null>>,
  control: AbstractControl,
  showAll: boolean,
  hasTopSelection: boolean
): Array<IOptionCategory<T | null>> {
  return categories.map((category) => ({
    ...category,
    options: arrangeOptions(category, control, showAll, hasTopSelection)
  }));
}
